"use client";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

export default function DigitalCourses() {
  const navigate = useNavigate();
  const {
    user,
    digitalMarketingGoals,
    digitalMarketingLoading,
    loadDigitalMarketingAllStructure,
    completedContent,
    enrollInDigitalMarketingCourse,
  } = useAuth();

  const [expandedGoal, setExpandedGoal] = useState(null);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [expandedModule, setExpandedModule] = useState(null);
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);
  const [localProgress, setLocalProgress] = useState({});
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());

  // Get student type and role from user context
  const courseSelection = user?.courseSelection || "digital_marketing";
  const isAdmin = user?.role === "admin"; // 🔥 NEW: Check if user is admin

  // 🔥 UPDATED: Admins always get access to the courses
  const hasDigitalAccess = courseSelection === "digital_marketing" || courseSelection === "all" || isAdmin;

  // Load digital marketing courses from backend
  useEffect(() => {
    if (hasDigitalAccess) {
      loadDigitalMarketingAllStructure().catch((err) => {
        console.error("Failed to load digital courses:", err);
      });
    }
  }, [hasDigitalAccess]);

  // Helper: Robust completion check (Checks ID in array AND internal flag)
  const checkIsContentCompleted = useCallback(
    (content) => {
      if (!content) return false;
      // Check 1: Is the ID in the global completedContent array?
      const isRx = completedContent.some(
        (id) => String(id) === String(content.id)
      );
      // Check 2: Does the content object itself say it's completed?
      const isDb = content.is_completed === true || content.is_completed === 1;

      return isRx || isDb;
    },
    [completedContent]
  );

  // Calculate progress for a single goal
  const calculateGoalProgress = useCallback((goal) => {
    if (!goal || !goal.modules) return 0;

    let total = 0;
    let completed = 0;

    goal.modules.forEach((module) => {
      module.topics?.forEach((topic) => {
        topic.subtopics?.forEach((subtopic) => {
          subtopic.content?.forEach((content) => {
            total++;
            if (checkIsContentCompleted(content)) {
              completed++;
            }
          });
        });
      });
    });

    return total === 0 ? 0 : (completed / total) * 100;
  }, [checkIsContentCompleted]);

  // Calculate progress for all goals strictly using local content state
  const calculateAllProgress = useCallback(() => {
    if (!digitalMarketingGoals || digitalMarketingGoals.length === 0) return;

    const progressMap = {};

    digitalMarketingGoals.forEach((goal) => {
      // Always calculate dynamically to ensure real-time local updates
      progressMap[goal.id] = calculateGoalProgress(goal);
    });

    setLocalProgress(progressMap);
    setLastUpdateTime(Date.now());
  }, [digitalMarketingGoals, calculateGoalProgress]);

  // Calculate progress whenever completedContent or goals change
  useEffect(() => {
    if (hasDigitalAccess && digitalMarketingGoals.length > 0) {
      calculateAllProgress();
    }
  }, [completedContent, digitalMarketingGoals, hasDigitalAccess, calculateAllProgress]);

  // Get goal progress 
  const getGoalProgress = useCallback((goal) => {
    if (localProgress[goal.id] !== undefined) {
      return localProgress[goal.id];
    }
    return calculateGoalProgress(goal);
  }, [localProgress, calculateGoalProgress]);

  // Calculate module progress
  const getModuleProgress = useCallback((module) => {
    if (!module || !module.topics) return 0;

    let totalContent = 0;
    let completedContentCount = 0;

    module.topics.forEach((topic) => {
      topic.subtopics?.forEach((subtopic) => {
        subtopic.content?.forEach((content) => {
          totalContent++;
          if (checkIsContentCompleted(content)) {
            completedContentCount++;
          }
        });
      });
    });

    return totalContent > 0 ? (completedContentCount / totalContent) * 100 : 0;
  }, [checkIsContentCompleted]);

  // Calculate topic progress
  const getTopicProgress = useCallback((topic) => {
    if (!topic || !topic.subtopics) return 0;

    let totalContent = 0;
    let completedContentCount = 0;

    topic.subtopics?.forEach((subtopic) => {
      subtopic.content?.forEach((content) => {
        totalContent++;
        if (checkIsContentCompleted(content)) {
          completedContentCount++;
        }
      });
    });

    return totalContent > 0 ? (completedContentCount / totalContent) * 100 : 0;
  }, [checkIsContentCompleted]);

  // 🔥 UPDATED: Admin bypasses sequential goal locks
  const isGoalLocked = useCallback((goalIndex) => {
    if (isAdmin) return false;

    if (goalIndex === 0) return false;

    for (let i = 0; i < goalIndex; i++) {
      const previousGoal = digitalMarketingGoals[i];
      if (previousGoal) {
        const previousGoalProgress = getGoalProgress(previousGoal);
        if (previousGoalProgress < 100) {
          return true;
        }
      }
    }
    return false;
  }, [digitalMarketingGoals, getGoalProgress, isAdmin]);

  const toggleGoal = (goalId, goalIndex) => {
    if (!hasDigitalAccess) {
      showNoAccessMessage();
      return;
    }
    if (isGoalLocked(goalIndex)) {
      showLockedMessage();
      return;
    }

    setExpandedGoal(expandedGoal === goalId ? null : goalId);
    setExpandedCourse(null);
    setExpandedModule(null);
    setExpandedTopic(null);
    setSelectedSubtopic(null);
  };

  const toggleModule = (moduleId, goalIndex) => {
    if (!hasDigitalAccess) {
      showNoAccessMessage();
      return;
    }
    if (isGoalLocked(goalIndex)) {
      showLockedMessage();
      return;
    }

    setExpandedModule(expandedModule === moduleId ? null : moduleId);
    setExpandedTopic(null);
    setSelectedSubtopic(null);
  };

  const toggleTopic = (topicId, goalIndex) => {
    if (!hasDigitalAccess) {
      showNoAccessMessage();
      return;
    }
    if (isGoalLocked(goalIndex)) {
      showLockedMessage();
      return;
    }

    setExpandedTopic(expandedTopic === topicId ? null : topicId);
    setSelectedSubtopic(null);
  };

  // Handle subtopic click - Navigate using content UUID
  const handleSubtopicClick = async (
    moduleId,
    subtopicId,
    subtopicName,
    contentUuid,
    goalName,
    courseName,
    goalIndex,
    contentItem = null
  ) => {
    if (!hasDigitalAccess) {
      showNoAccessMessage();
      return;
    }
    if (isGoalLocked(goalIndex)) {
      showLockedMessage();
      return;
    }

    setSelectedSubtopic(subtopicName);

    // Determine content type accurately from contentItem
    let contentType = "video"; // Default fallback
    let videoTitle = null;
    let cheatsheetTitle = null;
    let mcqTitle = null;

    if (contentItem) {
      // PRIORITY: Use the actual content_type from database
      if (contentItem.content_type) {
        contentType = contentItem.content_type;
      }
      // Fallback: Check based on titles if type is missing
      else if (contentItem.cheatsheet_title) {
        contentType = "cheatsheet";
      } else if (contentItem.mcq_title) {
        contentType = "mcq";
      }

      videoTitle = contentItem.video_title;
      cheatsheetTitle = contentItem.cheatsheet_title;
      mcqTitle = contentItem.mcq_title;
    }

    // Navigate using content UUID from backend
    if (contentUuid) {
      navigate(`/digital/content/${contentUuid}`, {
        state: {
          goalId: digitalMarketingGoals[goalIndex]?.id,
          moduleId,
          subtopicId,
          subtopicName,
          goalName,
          courseName,
          goalIndex,
          fromCourse: true,
          isDigital: true,
          contentType: contentType,
          video_title: videoTitle,
          cheatsheet_title: cheatsheetTitle,
          mcq_title: mcqTitle,
          contentUuid: contentUuid,
          contentItem: contentItem,
        },
      });
    }
  };

  const showLockedMessage = () => {
    alert("This content is locked. Complete previous goals first.");
  };

  const showNoAccessMessage = () => {
    alert(
      "You don't have access to Digital Marketing. Please update your profile selection."
    );
  };

  // Show loading state
  if (digitalMarketingLoading) {
    return (
      <div className="courses-container" style={{ marginTop: "50px" }}>
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  // Show access denied message if user doesn't have digital access
  if (!hasDigitalAccess) {
    return (
      <div className="courses-container" style={{ marginTop: "50px" }}>
        <div className="access-denied-container">
          <img
            src="/assets/img/locked_image.png"
            alt="Access Denied"
            className="locked_image"
          />
          <h3>Access Denied</h3>
          <p>You don't have access to Digital Marketing courses.</p>
        </div>
      </div>
    );
  }

  // Show empty state if no courses
  if (!digitalMarketingGoals || digitalMarketingGoals.length === 0) {
    return (
      <div className="courses-container" style={{ marginTop: "50px" }}>
        <div className="digital-header">
          <h1>Digital Marketing Mastery</h1>
          <p>Learn SEO, Social Media, PPC, Analytics & more</p>
        </div>
        <div className="no-courses-container">
          <h3>No courses available</h3>
          <p>Please check back later or contact support.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="courses-container"
      style={{ marginTop: "50px" }}
      key={`digital-courses-${lastUpdateTime}-${completedContent.length}`}
    >
      {/* Goals List */}
      <div className="goals-wrapper digital-goals-wrapper">
        {digitalMarketingGoals.map((goal, goalIndex) => {
          const goalPercent = getGoalProgress(goal);
          const locked = isGoalLocked(goalIndex);
          // 🔥 UPDATED: Admins are auto-enrolled visually
          const isEnrolled = goal.is_enrolled || isAdmin;

          return (
            <section
              className={`goal-group ${locked ? "goal-locked" : ""}`}
              key={goal.id}
            >
              <div
                className="goal-rail"
                style={{ backgroundColor: goal.color || "#9c27b0" }}
                aria-hidden="true"
              />
              <header
                className={`goal-header ${locked ? "goal-header-locked" : ""}`}
                onClick={() => toggleGoal(goal.id, goalIndex)}
              >
                <div className="goal-title-wrap">
                  <h2
                    className="goal-title"
                    style={{ color: goal.color || "#9c27b0" }}
                  >
                    {goal.name || goal.title}
                    {!isEnrolled && (
                      <span className="not-enrolled-tag"> Not Enrolled</span>
                    )}
                    {locked && <span className="locked-tag"> 🔒</span>}
                  </h2>
                  {goal.dateRange ? (
                    <span className="goal-dates">({goal.dateRange})</span>
                  ) : null}
                </div>
                <div className="goal-meta">
                  <div className="progress-section">
                    <div className="progress">
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{
                          width: `${goalPercent}%`,
                          backgroundColor: goal.color || "#9c27b0",
                        }}
                      />
                    </div>
                    <span
                      className="progress-percent"
                      style={{ color: goal.color || "#9c27b0" }}
                    >{`${goalPercent.toFixed(1)}%`}</span>
                  </div>
                </div>
              </header>

              <div className="goal-body">
                {!isEnrolled ? (
                  <div className="enroll-prompt">
                    <p>You are not enrolled in this course.</p>
                    <button
                      className="enroll-button"
                      onClick={async () => {
                        const result = await enrollInDigitalMarketingCourse(
                          goal.id
                        );
                        if (result.success) {
                          alert("Successfully enrolled!");
                          loadDigitalMarketingAllStructure();
                        }
                      }}
                    >
                      Enroll Now
                    </button>
                  </div>
                ) : !locked && (!goal.modules || goal.modules.length === 0) ? (
                  <div className="no-courses">
                    <h4>No modules found</h4>
                    <p>This course is currently being prepared.</p>
                  </div>
                ) : (
                  goal.modules?.map((module) => {
                    const moduleProgress = getModuleProgress(module);
                    const isModuleCompleted = moduleProgress >= 100;
                    const isExpandedModule = expandedModule === module.id;

                    return (
                      <div className="courses" key={module.id}>
                        {/* Course Header */}
                        <div className="couses-and-status">
                          <h4 style={{ color: "inherit" }}>{module.name}</h4>
                          <div className="progress-section_module">
                            <div
                              className={`circular-progress ${isModuleCompleted ? "completed" : ""}`}
                              style={{ "--progress": moduleProgress }}
                            >
                              <span className="progress-value">
                                {moduleProgress.toFixed(1)}%
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Expand Modules Button */}
                        <div className="active-module_course">
                          <button
                            onClick={() =>
                              locked
                                ? showLockedMessage()
                                : toggleModule(module.id, goalIndex)
                            }
                          >
                            {isExpandedModule ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22"
                                height="22"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                              >
                                <path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8" />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22"
                                height="22"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                              >
                                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4" />
                              </svg>
                            )}
                          </button>
                          <p
                            onClick={() =>
                              locked
                                ? showLockedMessage()
                                : toggleModule(module.id, goalIndex)
                            }
                            style={{ cursor: "pointer", color: "inherit" }}
                          >
                            Active Modules
                          </p>
                        </div>

                        {/* Topics Section */}
                        {isExpandedModule && !locked && (
                          <div className="module-details">
                            {module.topics?.map((topic) => {
                              const topicProgress = getTopicProgress(topic);
                              const isTopicExpanded =
                                expandedTopic === topic.id;
                              const isTopicCompleted = topicProgress >= 100;

                              return (
                                <div
                                  key={topic.id}
                                  className="module-container"
                                >
                                  <div
                                    className="module-single-div"
                                    onClick={() =>
                                      toggleTopic(topic.id, goalIndex)
                                    }
                                  >
                                    <div className="timeline">
                                      <div className="circle-row module-circle-row">
                                        <div
                                          className={`circle module-circle ${isTopicCompleted ? "completed" : ""}`}
                                          style={{
                                            "--progress": `${topicProgress}%`,
                                          }}
                                        >
                                          {isTopicCompleted ? "✓" : ""}
                                        </div>
                                      </div>
                                      {isTopicExpanded && (
                                        <>
                                          {topic.subtopics?.flatMap(
                                            (subtopic) =>
                                              subtopic.content?.map(
                                                (content) => {
                                                  const isCompleted =
                                                    checkIsContentCompleted(
                                                      content
                                                    );

                                                  return (
                                                    <div
                                                      className="circle-row subtopic-circle-row"
                                                      key={content.id}
                                                    >
                                                      <div
                                                        className={`circle subtopic-circle ${isCompleted
                                                          ? "completed"
                                                          : ""
                                                          }`}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleSubtopicClick(
                                                            module.id,
                                                            subtopic.id,
                                                            subtopic.name,
                                                            content.content_uuid,
                                                            goal.name ||
                                                            goal.title,
                                                            module.name,
                                                            goalIndex,
                                                            content
                                                          );
                                                        }}
                                                      >
                                                        {isCompleted ? "✓" : ""}
                                                      </div>
                                                    </div>
                                                  );
                                                }
                                              )
                                          )}

                                          {/* vertical connector */}
                                          {topic.subtopics?.length > 0 && (
                                            <div className="vertical-line"></div>
                                          )}
                                        </>
                                      )}
                                    </div>

                                    <div className="content-area">
                                      <div className="module_topic_names">
                                        <div className="module-header-row">
                                          <div className="topic-label">
                                            <h6>DIGITAL TOPIC</h6>
                                          </div>
                                          <div className="module-title">
                                            <h5>{topic.name}</h5>
                                          </div>
                                        </div>

                                        <div className="active-module_subtopic">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleTopic(topic.id, goalIndex);
                                            }}
                                          >
                                            {isTopicExpanded ? (
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="22"
                                                height="22"
                                                fill="currentColor"
                                                viewBox="0 0 16 16"
                                              >
                                                <path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8" />
                                              </svg>
                                            ) : (
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="22"
                                                height="22"
                                                fill="currentColor"
                                                viewBox="0 0 16 16"
                                              >
                                                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4" />
                                              </svg>
                                            )}
                                          </button>
                                        </div>
                                      </div>

                                      {/* Subtopics Section */}
                                      {isTopicExpanded && (
                                        <div className="subtopics-section">
                                          {topic.subtopics?.map((subtopic) =>
                                            subtopic.content?.map((content) => {
                                              const isCompleted =
                                                checkIsContentCompleted(
                                                  content
                                                );
                                              return (
                                                <div
                                                  className={`subtopic-content-row ${isCompleted ? "completed" : ""}`}
                                                  key={content.id}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSubtopicClick(
                                                      module.id,
                                                      subtopic.id,
                                                      subtopic.name,
                                                      content.content_uuid,
                                                      goal.name || goal.title,
                                                      module.name,
                                                      goalIndex,
                                                      content // Pass the full content object
                                                    );
                                                  }}
                                                >
                                                  <span className="subtopic-text">
                                                    {content.video_title ||
                                                      content.cheatsheet_title ||
                                                      content.mcq_title ||
                                                      subtopic.name}
                                                  </span>
                                                </div>
                                              );
                                            })
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}